import { ErrorState, TableLoadingOverlay, Surface, Alert } from '@/components'
import { BenchmarkingChart } from '@/features/Analytics/components/BenchmarkingChart/BenchmarkingChart'
import {
    BenchmarkingRequestParams,
    BenchmarkingReportSettings,
    useFiltersApi,
    BenchmarkingItemWithUnit,
    useAnalyticsApi,
} from '@/hooks'
import { ViewType } from '@/stores/useViewAsToggle/useViewAsToggle'
import { getUnitNameByUnitId } from '@/utils/getUnitNameByUnitId'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useBenchmarkingDisplayManager } from './useBenchmarkingDisplayManager'
import { Timeline } from '@/utils/date'
import { ContentPlaceholder } from '@/components/molecules'
import { BenchmarkingTableContainer } from '../BenchmarkingTableContainer/BenchmarkingTableContainer'

type BenchmarkingReportViewProps = {
    filters: BenchmarkingRequestParams
    activeView: ViewType
    reportSettings: BenchmarkingReportSettings
    baselineYearTimeline?: Timeline
    previousYearTimeline?: Timeline
}

export const BenchmarkingReportView: React.FC<BenchmarkingReportViewProps> = ({
    filters,
    activeView,
    reportSettings,
    baselineYearTimeline,
    previousYearTimeline,
}) => {
    const { t } = useTranslation('translations')
    const { useBenchmarkingQuery } = useAnalyticsApi()

    const { useCommoditiesQuery } = useFiltersApi()

    const { data: commodities } = useCommoditiesQuery({
        'nodeIds[]': filters['nodeIds[]'],
    })

    const { showAlert } = useBenchmarkingDisplayManager(reportSettings, filters)

    const {
        data: benchmarkingData,
        isError,
        isLoading,
        isFetching,
        isFetched,
    } = useBenchmarkingQuery(filters)

    const { data: currencies } = useCurrencies()

    const data = React.useMemo(() => {
        if (!benchmarkingData || !commodities) return null

        return benchmarkingData.map((item) => ({
            ...item,
            usage: {
                ...item.usage,
                unit: getUnitNameByUnitId(item.usage.unitId, commodities),
            },
            cost: {
                ...item.cost,
                currency: currencies.find(
                    (currency) => currency.currencyId === item.cost.currencyId
                )?.currency,
            },
        })) as BenchmarkingItemWithUnit[]
    }, [benchmarkingData, commodities, currencies])

    const hasData = Array.isArray(data) && data.length > 0

    const loadingMessage =
        activeView === 'chart'
            ? t('analytics.common.report_chart_loading')
            : t('analytics.common.report_table_loading')

    const emptyStateDescription = t(
        'analytics.common.report_empty_state.description'
    )

    if (isError) {
        return <ErrorState description={t('analytics.common.report_error')} />
    }

    if (isFetched && !hasData) {
        return (
            <ContentPlaceholder
                title={t('analytics.common.report_empty_state.title')}
                description={emptyStateDescription}
                illustrationSrc="/assets/empty-state-illustration.svg"
                name="empty-state"
            />
        )
    }

    const emptyData: BenchmarkingItemWithUnit[] = Array.from(new Array(10)).map(
        () => {
            return {
                entityId: '',
                cost: {
                    currency: '',
                },
                usage: {
                    unit: '',
                },
            } as unknown as BenchmarkingItemWithUnit
        }
    )

    return (
        <div className="relative">
            {activeView === 'chart' ? (
                <div className="grid h-full">
                    <Surface className="min-w-minWidgetWidth">
                        <div className="flex h-full flex-col gap-4">
                            {showAlert && (
                                <Alert
                                    variant="info"
                                    description={t(
                                        'analytics.benchmarking.alert'
                                    )}
                                />
                            )}
                            <BenchmarkingChart
                                data={data ?? emptyData}
                                reportSettings={reportSettings}
                                baselineYearTimeline={baselineYearTimeline}
                                previousYearTimeline={previousYearTimeline}
                                currentTimeline={{
                                    startDate: filters.startDate,
                                    endDate: filters.endDate,
                                }}
                            />
                        </div>
                    </Surface>
                </div>
            ) : (
                <div className="min-h-widgetHeight">
                    <BenchmarkingTableContainer
                        data={data ?? emptyData}
                        reportSettings={reportSettings}
                        baselineYearTimeline={baselineYearTimeline}
                        previousYearTimeline={previousYearTimeline}
                        currentTimeline={{
                            startDate: filters.startDate,
                            endDate: filters.endDate,
                        }}
                    />
                </div>
            )}

            <TableLoadingOverlay
                show={isLoading || isFetching || !isFetched}
                title={loadingMessage}
            />
        </div>
    )
}

const useCurrencies = () => {
    return {
        data: [
            {
                currencyId: 1,
                currency: '$',
            },
        ],
    }
}
